import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pin, Archive, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

// Import sub-components
import { 
  noteColors, 
  getText, 
  NoteCard, 
  AddNoteDialog, 
  EditNoteDialog, 
  ArchiveDialog, 
  BoardsDialog,
  PinboardGamification
} from './pinboard';

const Pinboard = ({ title, language = 'de' }) => {
  // Boards state
  const [boards, setBoards] = useState([]);
  const [currentBoardIndex, setCurrentBoardIndex] = useState(0);
  const [isBoardDialogOpen, setIsBoardDialogOpen] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [pinnedTasks, setPinnedTasks] = useState([]);
  const [pinnedProjects, setPinnedProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  
  // New note form state
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteColor, setNewNoteColor] = useState('yellow');
  const [newNoteCategory, setNewNoteCategory] = useState('');
  const [newNoteImage, setNewNoteImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Edit state
  const [editingNote, setEditingNote] = useState(null);
  
  // Swipe handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const boardRef = useRef(null);

  const minSwipeDistance = 50;
  const currentBoard = boards[currentBoardIndex] || { id: 'default', name: getText('title', language) };

  // ============ API CALLS ============
  
  const fetchBoards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/boards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBoards(data.boards || [{ id: 'default', name: getText('title', language) }]);
      }
    } catch (error) {
      console.error('Error fetching boards:', error);
      setBoards([{ id: 'default', name: getText('title', language) }]);
    }
  };

  const fetchNotes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const boardId = currentBoard?.id || 'default';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/notes?board_id=${boardId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
        setPinnedTasks(data.pinned_tasks || []);
        setPinnedProjects(data.pinned_projects || []);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentBoard?.id]);

  const fetchArchivedNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/archived`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setArchivedNotes(data.notes || []);
      }
    } catch (error) {
      console.error('Error fetching archived notes:', error);
    }
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  useEffect(() => {
    if (boards.length > 0) {
      fetchNotes();
    }
  }, [boards, currentBoardIndex, fetchNotes]);

  // ============ IMAGE COMPRESSION ============
  
  const compressImage = (file, maxSizeKB = 200) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const maxDimension = 800;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.8;
          let result = canvas.toDataURL('image/jpeg', quality);
          
          while (result.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            result = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(result);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'de' ? 'Nur Bilder erlaubt' : 'Only images allowed');
      return;
    }
    
    try {
      const compressed = await compressImage(file);
      if (isEdit && editingNote) {
        setEditingNote({ ...editingNote, image: compressed });
      } else {
        setNewNoteImage(compressed);
        setImagePreview(compressed);
      }
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler beim Laden des Bildes' : 'Error loading image');
    }
  };

  // ============ SWIPE HANDLERS ============
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentBoardIndex < boards.length - 1) {
      setCurrentBoardIndex(prev => prev + 1);
    } else if (isRightSwipe && currentBoardIndex > 0) {
      setCurrentBoardIndex(prev => prev - 1);
    }
  };

  // ============ NOTE HANDLERS ============
  
  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newNoteContent,
          color: newNoteColor,
          category: newNoteCategory,
          board_id: currentBoard.id,
          image: newNoteImage
        })
      });

      if (response.ok) {
        const data = await response.json();
        setNotes([...notes, data]);
        resetAddForm();
        setIsAddDialogOpen(false);
        toast.success(language === 'de' ? 'Notiz hinzugefügt' : 'Note added');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(language === 'de' ? 'Fehler beim Hinzufügen' : 'Error adding note');
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/notes/${editingNote.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: editingNote.content,
          color: editingNote.color,
          category: editingNote.category,
          image: editingNote.image
        })
      });

      if (response.ok) {
        setNotes(notes.map(n => n.id === editingNote.id ? editingNote : n));
        setIsEditDialogOpen(false);
        setEditingNote(null);
        toast.success(language === 'de' ? 'Notiz aktualisiert' : 'Note updated');
      }
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setNotes(notes.filter(n => n.id !== noteId));
        setArchivedNotes(archivedNotes.filter(n => n.id !== noteId));
        setIsEditDialogOpen(false);
        setEditingNote(null);
        toast.success(language === 'de' ? 'Notiz gelöscht' : 'Note deleted');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleMarkDone = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_completed: true })
      });

      if (response.ok) {
        setNotes(notes.filter(n => n.id !== noteId));
        setIsEditDialogOpen(false);
        setEditingNote(null);
        toast.success(language === 'de' ? 'Notiz erledigt' : 'Note completed');
      }
    } catch (error) {
      console.error('Error marking note as done:', error);
    }
  };

  const handleRestoreNote = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/notes/${noteId}/restore`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setArchivedNotes(archivedNotes.filter(n => n.id !== noteId));
        fetchNotes();
        toast.success(language === 'de' ? 'Notiz wiederhergestellt' : 'Note restored');
      }
    } catch (error) {
      console.error('Error restoring note:', error);
    }
  };

  // ============ BOARD HANDLERS ============
  
  const handleAddBoard = async () => {
    if (!newBoardName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/boards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newBoardName })
      });

      if (response.ok) {
        const data = await response.json();
        setBoards([...boards, data]);
        setNewBoardName('');
        setIsBoardDialogOpen(false);
        setCurrentBoardIndex(boards.length);
        toast.success(language === 'de' ? 'Pinwand erstellt' : 'Board created');
      }
    } catch (error) {
      console.error('Error creating board:', error);
      toast.error(language === 'de' ? 'Fehler beim Erstellen' : 'Error creating board');
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (boardId === 'default') {
      toast.error(getText('cannotDeleteDefault', language));
      return;
    }
    
    if (!window.confirm(language === 'de' ? 'Pinwand und alle Notizen löschen?' : 'Delete board and all notes?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pinboard/boards/${boardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const newBoards = boards.filter(b => b.id !== boardId);
        setBoards(newBoards);
        if (currentBoardIndex >= newBoards.length) {
          setCurrentBoardIndex(Math.max(0, newBoards.length - 1));
        }
        toast.success(language === 'de' ? 'Pinwand gelöscht' : 'Board deleted');
      }
    } catch (error) {
      console.error('Error deleting board:', error);
    }
  };

  // ============ HELPERS ============
  
  const resetAddForm = () => {
    setNewNoteContent('');
    setNewNoteColor('yellow');
    setNewNoteCategory('');
    setNewNoteImage(null);
    setImagePreview(null);
  };

  const openEditDialog = (note) => {
    setEditingNote({ ...note });
    setIsEditDialogOpen(true);
  };

  const openArchiveDialog = () => {
    fetchArchivedNotes();
    setIsArchiveDialogOpen(true);
  };

  // ============ RENDER ============
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#006039]"></div>
      </div>
    );
  }

  const allItems = [...notes, ...pinnedProjects, ...pinnedTasks.filter(t => !t.completed)];

  return (
    <section 
      className="py-4"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with board navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          {/* Left side: Title and navigation */}
          <div className="flex items-center gap-2 min-w-0">
            {title !== null && <Pin className="h-5 w-5 text-[#006039] flex-shrink-0" />}
            <div className="flex items-center gap-1 min-w-0">
              {/* Previous board button */}
              {boards.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => setCurrentBoardIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentBoardIndex === 0}
                  data-testid="pinboard-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              )}
              
              {/* Board name - only show if title prop is provided or there are multiple boards */}
              {(title !== null || boards.length > 1) && (
                <h2 className="text-base sm:text-xl font-serif font-bold text-slate-900 dark:text-white truncate" data-testid="pinboard-name">
                  {boards.length > 1 ? currentBoard.name : (title || getText('title', language))}
                </h2>
              )}
              
              {/* Next board button */}
              {boards.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => setCurrentBoardIndex(prev => Math.min(boards.length - 1, prev + 1))}
                  disabled={currentBoardIndex === boards.length - 1}
                  data-testid="pinboard-next-btn"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              )}
              
              {/* Board indicator dots */}
              {boards.length > 1 && (
                <div className="flex gap-1 sm:gap-1.5 ml-1 sm:ml-2 flex-shrink-0">
                  {boards.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentBoardIndex(idx)}
                      className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-colors ${
                        idx === currentBoardIndex ? 'bg-[#006039]' : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                      data-testid={`pinboard-dot-${idx}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Right side: Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Board management dialog */}
            <Dialog open={isBoardDialogOpen} onOpenChange={setIsBoardDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-2 sm:px-3 gap-1 sm:gap-2" data-testid="boards-btn">
                  <Layers className="h-4 w-4" />
                  <span className="hidden sm:inline">{getText('boards', language)}</span>
                </Button>
              </DialogTrigger>
            </Dialog>
            
            <BoardsDialog
              isOpen={isBoardDialogOpen}
              onOpenChange={setIsBoardDialogOpen}
              language={language}
              boards={boards}
              currentBoardIndex={currentBoardIndex}
              newBoardName={newBoardName}
              setNewBoardName={setNewBoardName}
              onSelectBoard={setCurrentBoardIndex}
              onAddBoard={handleAddBoard}
              onDeleteBoard={handleDeleteBoard}
            />
            
            {/* Archive Button */}
            <Button variant="outline" size="sm" onClick={openArchiveDialog} className="h-8 px-2 sm:px-3 gap-1 sm:gap-2" data-testid="archive-btn">
              <Archive className="h-4 w-4" />
              <span className="hidden sm:inline">{getText('archive', language)}</span>
            </Button>
            
            {/* Add Note Button */}
            <Button 
              size="sm" 
              className="h-8 px-2 sm:px-3 gap-1 sm:gap-2 bg-[#006039] hover:bg-[#004d2e]"
              onClick={() => setIsAddDialogOpen(true)}
              data-testid="add-note-btn"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden xs:inline">{getText('addNote', language)}</span>
            </Button>
          </div>
        </div>

        {/* Cork board background */}
        <div
          ref={boardRef}
          className="relative min-h-[300px] rounded-xl p-6 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #c4a574 0%, #b5956a 50%, #a6845f 100%)',
            boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.3), 0 4px 20px rgba(0,0,0,0.2)'
          }}
        >
          {/* Cork texture overlay */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Notes grid */}
          <AnimatePresence>
            {allItems.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center h-48 text-white/80"
              >
                <Pin className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-center">{getText('noNotes', language)}</p>
              </motion.div>
            ) : (
              <div className="relative flex flex-wrap gap-4 justify-center sm:justify-start">
                {allItems.map((item) => (
                  <NoteCard
                    key={item.id}
                    note={item}
                    onClick={openEditDialog}
                  />
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Swipe hint for mobile */}
        {boards.length > 1 && (
          <p className="text-xs text-muted-foreground mt-2 text-center sm:hidden">
            {getText('swipeHint', language)}
          </p>
        )}
        
        {/* Gamification Section */}
        <div className="mt-6">
          <PinboardGamification language={language} />
        </div>
      </div>

      {/* Dialogs */}
      <AddNoteDialog
        isOpen={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) resetAddForm();
        }}
        language={language}
        content={newNoteContent}
        setContent={setNewNoteContent}
        category={newNoteCategory}
        setCategory={setNewNoteCategory}
        color={newNoteColor}
        setColor={setNewNoteColor}
        imagePreview={imagePreview}
        onImageSelect={(e) => handleImageSelect(e, false)}
        onImageRemove={() => {
          setNewNoteImage(null);
          setImagePreview(null);
        }}
        onSave={handleAddNote}
      />

      <EditNoteDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        language={language}
        note={editingNote}
        setNote={setEditingNote}
        onSave={handleUpdateNote}
        onDelete={handleDeleteNote}
        onMarkDone={handleMarkDone}
        onImageSelect={(e) => handleImageSelect(e, true)}
      />

      <ArchiveDialog
        isOpen={isArchiveDialogOpen}
        onOpenChange={setIsArchiveDialogOpen}
        language={language}
        archivedNotes={archivedNotes}
        onRestore={handleRestoreNote}
        onDelete={handleDeleteNote}
      />
    </section>
  );
};

export default Pinboard;
